"use client"

import { useState, useEffect, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { PhoneIcon, ShieldCheckIcon, ArrowLeftIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon } from "@heroicons/react/24/outline"

import { auth, setupRecaptcha, signInWithPhoneNumber } from "../../../firebaseConfig"
import { CheckPhoneVerifyAsync } from "../Slice/PhoneVerified/CheckPhoneVerifySlice"
import { checkPhoneVerificationPublicAsync, resetPhoneVerificationState } from "../Slice/PhoneVerificationSignup/PhoneVerificationSignupSlice"
import { fetchProfileAsync, selectCompanyProfile } from "../../employerDashboard/employerDashAPIs/companyProfile/CompanyProfileSlice"
import { fetchCookie } from "../../common/FetchCookie"

function PhoneVerificationPhone() {
    const location = useLocation()
    const [mobile, setMobile] = useState("")
    const [otp, setOtp] = useState("")
    const [confirmationResult, setConfirmationResult] = useState(null)
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [successMessage, setSuccessMessage] = useState("")
    const [isPhoneVerified, setIsPhoneVerified] = useState(false)
    const [otpSent, setOtpSent] = useState(false)
    const basicdetails = useSelector(selectCompanyProfile)

    // Auth type state - 'form' or 'oauth'
    const [authType, setAuthType] = useState(null)
    const [isAuthTypeLoaded, setIsAuthTypeLoaded] = useState(false)
    const [isProfileLoaded, setIsProfileLoaded] = useState(false)

    // Phone number already exists state (for checking if phone is registered with another account)
    const [phoneAlreadyExists, setPhoneAlreadyExists] = useState(false)

    // Track if phone exists in profile
    const [hasPhoneInProfile, setHasPhoneInProfile] = useState(false)

    // Resend OTP timer states
    const [resendTimer, setResendTimer] = useState(0)
    const [canResend, setCanResend] = useState(false)

    const dispatch = useDispatch()
    const navigate = useNavigate()

    // Phone verification state from Redux
    const {
        loading: phoneLoading,
        success: phoneVerifiedRedux,
        error: phoneReduxError,
    } = useSelector((state) => state.phoneVerificationPublic || {})

    // Timer countdown effect
    useEffect(() => {
        let interval = null
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => {
                    if (prev <= 1) {
                        setCanResend(true)
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)
        }
        return () => {
            if (interval) clearInterval(interval)
        }
    }, [resendTimer])

    // Format timer display
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    // Fetch auth type and user data from cookie on mount
    useEffect(() => {
        const getAuthType = async () => {
            try {
                const userData = await fetchCookie()
                const registerType = userData?.user?.register_type || userData?.register_type

                // Handle both 'oauth' and 'google' as OAuth type
                if (registerType === 'oauth' || registerType === 'google') {
                    setAuthType('oauth')
                } else {
                    setAuthType('form')
                }

            } catch (error) {
                console.error("Error fetching auth type:", error)
                setAuthType('form') // Default to 'form' on error
            } finally {
                setIsAuthTypeLoaded(true)
            }
        }
        getAuthType()
    }, [])

    // Setup recaptcha when component mounts
    useEffect(() => {
        const initializeRecaptcha = () => {
            try {
                setupRecaptcha()
            } catch (error) {
                console.error("Error initializing recaptcha:", error)
            }
        }
        const timer = setTimeout(initializeRecaptcha, 100)
        return () => {
            clearTimeout(timer)
            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear()
                window.recaptchaVerifier = null
            }
        }
    }, [])

    // Reset verification states and fetch profile on component mount
    useEffect(() => {
        dispatch(resetPhoneVerificationState())
        dispatch(fetchProfileAsync())
    }, [dispatch])

    // Prefill phone from basicdetails (profile API)
    useEffect(() => {
        if (basicdetails !== null && basicdetails !== undefined) {
            // Check if phone exists and is not null/empty
            const phoneFromProfile = basicdetails.phone || basicdetails.mobile
            const hasPhone = phoneFromProfile && phoneFromProfile.toString().trim() !== "" && phoneFromProfile.toString() !== "null"
            
            setHasPhoneInProfile(hasPhone)
            
            if (hasPhone) {
                setMobile(String(phoneFromProfile))
            }
            
            // Check if already verified from API response
            if (basicdetails.user?.phone_verify === 1) {
                setIsPhoneVerified(true)
            }
            
            setIsProfileLoaded(true)
        }
    }, [basicdetails])

    // Handle phone verification success - redirect to employer dashboard
    useEffect(() => {
        if (phoneVerifiedRedux) {
            setIsPhoneVerified(true)
            setSuccessMessage("Phone verification successful!")
            setOtp("")
            setTimeout(() => {
                // Always redirect to employer dashboard for this component
                navigate("/employer-dashboard");
            }, 1500);
        }
    }, [phoneVerifiedRedux, navigate])

    // Handle phone verification error
    useEffect(() => {
        if (phoneReduxError) {
            setError(phoneReduxError)
        }
    }, [phoneReduxError])

    useEffect(() => {
       
    }, [authType, mobile, isPhoneVerified, hasPhoneInProfile])

    const formatPhoneNumber = (phone) => {
        if (!phone) return ""
        const cleaned = phone.toString().replace(/\D/g, "")
        if (phone.toString().startsWith("+91")) {
            return phone.toString()
        }
        if (cleaned.length === 10) {
            return `+91${cleaned}`
        }
        if (cleaned.startsWith("91") && cleaned.length === 12) {
            return `+${cleaned}`
        }
        return `+91${cleaned}`
    }

    const validatePhoneNumber = (phone) => {
        if (!phone) return false
        const cleaned = phone.toString().replace(/\D/g, "")
        return cleaned.length === 10 || (cleaned.length === 12 && cleaned.startsWith("91"))
    }

    // Helper function to send OTP via Firebase
    const sendOtpViaFirebase = async (phoneNumber) => {
        try {

            if (!window.recaptchaVerifier) {
                setupRecaptcha()
                await new Promise((resolve) => setTimeout(resolve, 500))
            }

            const appVerifier = window.recaptchaVerifier
            if (!appVerifier) {
                throw new Error("Recaptcha verifier not initialized")
            }

            const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier)
            setConfirmationResult(confirmation)
            setSuccessMessage("OTP sent successfully!")
            setOtpSent(true)
            setPhoneAlreadyExists(false)
            
            // Start the resend timer (120 seconds)
            setResendTimer(120)
            setCanResend(false)
        } catch (otpError) {
            console.error("OTP Error:", otpError)
            if (otpError.code === "auth/invalid-phone-number") {
                setError("Invalid phone number format. Please check and try again.")
            } else if (otpError.code === "auth/too-many-requests") {
                setError("Too many requests. Please try again later.")
            } else if (otpError.code === "auth/quota-exceeded") {
                setError("SMS quota exceeded. Please try again later.")
            } else {
                setError("Error sending OTP. Please try again.")
            }

            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear()
                window.recaptchaVerifier = null
            }

            try {
                setupRecaptcha()
            } catch (setupError) {
                console.error("Error reinitializing recaptcha:", setupError)
            }
        }
    }

    // Handle phone number change
    const handlePhoneChange = (e) => {
        const newPhone = e.target.value
        setMobile(newPhone)
        // Reset states when user changes phone number
        setError("")
        setSuccessMessage("")
        setPhoneAlreadyExists(false)
        setOtpSent(false)
        setConfirmationResult(null)
        setOtp("")
        setResendTimer(0)
        setCanResend(false)
    }

    const sendOtp = async (e) => {
        e.preventDefault()
        setError("")
        setSuccessMessage("")
        setPhoneAlreadyExists(false)
        setLoading(true)
        const latestMobile = mobile.toString().trim()

        if (!latestMobile) {
            setError("Please enter a valid phone number.")
            setLoading(false)
            return
        }

        if (!validatePhoneNumber(latestMobile)) {
            setError("Please enter a valid 10-digit phone number.")
            setLoading(false)
            return
        }

        const phoneNumber = formatPhoneNumber(latestMobile)

        // Determine the flow based on whether phone exists in profile and auth type
        if (!hasPhoneInProfile) {
            // No phone in profile - user is entering a new number
            try {
                const result = await dispatch(CheckPhoneVerifyAsync(latestMobile)).unwrap()

                if (result?.code === 200) {
                    setPhoneAlreadyExists(true)
                    setError("This phone number is already registered with another account. Please enter a different phone number.")
                    setLoading(false)
                    return
                }
            } catch (err) {
                console.warn("Phone not found in database, proceeding with OTP:", err.message)
                setPhoneAlreadyExists(false)
            }

            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP:", err)
            }
        } else if (authType === 'form') {
            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP for form signup:", err)
            }
        } else if (authType === 'oauth') {
            try {
                const result = await dispatch(CheckPhoneVerifyAsync(latestMobile)).unwrap()

                if (result?.code === 200) {
                    setPhoneAlreadyExists(true)
                    setError("This phone number is already registered with another account. Please enter a different phone number.")
                    setLoading(false)
                    return
                }
            } catch (err) {
                console.warn("Phone not verified in database, proceeding with OTP:", err.message)
                setPhoneAlreadyExists(false)
            }

            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP for OAuth signup:", err)
            }
        } else {
            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP:", err)
            }
        }

        setLoading(false)
    }

    // Resend OTP function
    const resendOtp = async (e) => {
        e.preventDefault()
        if (!canResend) return
        
        setError("")
        setSuccessMessage("")
        setLoading(true)
        setOtp("") // Clear the OTP input
        
        const phoneNumber = formatPhoneNumber(mobile.toString().trim())
        
        // Clear and reinitialize recaptcha for resend
        if (window.recaptchaVerifier) {
            window.recaptchaVerifier.clear()
            window.recaptchaVerifier = null
        }
        
        try {
            setupRecaptcha()
            await new Promise((resolve) => setTimeout(resolve, 500))
            await sendOtpViaFirebase(phoneNumber)
        } catch (err) {
            console.error("Error resending OTP:", err)
            setError("Failed to resend OTP. Please try again.")
        }
        
        setLoading(false)
    }

    const verifyOtp = async (e) => {
        e.preventDefault()
        setError("")
        setSuccessMessage("")
        setLoading(true)

        if (!otp.trim()) {
            setError("Please enter the OTP.")
            setLoading(false)
            return
        }

        if (!confirmationResult) {
            setError("Please request OTP first.")
            setLoading(false)
            return
        }

        try {
            const result = await confirmationResult.confirm(otp.trim())

            const phoneNumber = formatPhoneNumber(mobile.toString().trim())

            await dispatch(checkPhoneVerificationPublicAsync({
                mobile: phoneNumber,
                code: otp.trim()
            })).unwrap()

        } catch (err) {
            console.error("OTP verification error:", err)
            if (err.code === "auth/invalid-verification-code") {
                setError("Invalid OTP. Please check and try again.")
            } else if (err.code === "auth/code-expired") {
                setError("OTP has expired. Please request a new one.")
            } else {
                setError(err.message || "Invalid OTP. Please try again.")
            }
        } finally {
            setLoading(false)
        }
    }

    // Go back to dashboard
    const handleGoBack = () => {
        navigate("/employer-dashboard")
    }

    const isPhoneFieldEditable = () => {
        if (!hasPhoneInProfile) return true
        if (authType === 'oauth') return true
        if (phoneAlreadyExists) return true
        return false
    }

    const isPhoneFieldDisabled = () => {
        if (loading) return true
        if (otpSent && !phoneAlreadyExists) return true
        if (isPhoneVerified) return true
        if (!isPhoneFieldEditable()) return true
        return false
    }

    const inputStyles =
        "h-12 px-4 w-full bg-white border border-gray-200 rounded-lg text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 ease-in-out placeholder-gray-400"
    const labelStyles = "block text-sm font-medium text-gray-700 mb-1.5"
    const buttonBaseStyles =
        "w-full flex justify-center items-center h-12 px-6 border border-transparent rounded-lg text-base font-medium text-white transition-all duration-200 ease-in-out"

    // Show loading state while fetching auth type or profile
    if (!isAuthTypeLoaded || !isProfileLoaded) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
                <div className="text-center">
                    <svg
                        className="animate-spin h-10 w-10 text-indigo-600 mx-auto"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        ></circle>
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        )
    }

    // Get helper text based on conditions
    const getHelperText = () => {
        if (phoneAlreadyExists) {
            return "This number is already registered. Please enter a different number."
        }
        if (!hasPhoneInProfile) {
            return "Enter your 10-digit mobile number to add and verify"
        }
        if (authType === 'form' && hasPhoneInProfile) {
            return "Phone number from your registration (cannot be changed)"
        }
        if (authType === 'oauth') {
            return "Enter your 10-digit mobile number without country code"
        }
        return "Enter 10-digit mobile number without country code"
    }

    return (
        <div
            className="min-h-screen bg-gradient-to-br from-indigo-50 to-white py-12 px-4 sm:px-6 lg:px-8"
            style={{ fontFamily: "'Inter', sans-serif" }}
        >
            <div className="max-w-xl mx-auto">
                {/* Back Button */}
                <button
                    onClick={handleGoBack}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors duration-200"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    Back to Dashboard
                </button>

                <div className="text-center mb-8">
                    <ShieldCheckIcon className="mx-auto h-12 w-12 text-indigo-600" />
                    <h2 className="mt-4 text-2xl font-extrabold text-gray-900">Phone Verification</h2>
                    <p className="mt-2 text-sm text-gray-600">
                        {hasPhoneInProfile 
                            ? "Verify your phone number to secure your account" 
                            : "Add and verify your phone number to secure your account"
                        }
                    </p>
                </div>

                <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
                    <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-blue-500">
                        <h3 className="text-lg font-semibold text-white">
                            {hasPhoneInProfile ? "Verify Your Phone Number" : "Add Your Phone Number"}
                        </h3>
                        <p className="text-indigo-200 text-sm">
                            {hasPhoneInProfile 
                                ? "We'll send a verification code to your phone" 
                                : "Enter your phone number and we'll send a verification code"
                            }
                        </p>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Phone Verification Section */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <div
                                        className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                            isPhoneVerified 
                                                ? "bg-green-100 text-green-600" 
                                                : phoneAlreadyExists 
                                                    ? "bg-red-100 text-red-600" 
                                                    : !hasPhoneInProfile 
                                                        ? "bg-blue-100 text-blue-600"
                                                        : "bg-gray-100 text-gray-500"
                                        }`}
                                    >
                                        <PhoneIcon className="h-4 w-4" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">Phone Number</span>
                                    {!hasPhoneInProfile && !isPhoneVerified && (
                                        <span className="text-xs text-blue-500 ml-1">(Not added yet)</span>
                                    )}
                                </div>
                                {isPhoneVerified && (
                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                        <CheckCircleIcon className="-ml-0.5 mr-1.5 h-3 w-3" aria-hidden="true" /> Verified
                                    </span>
                                )}
                            </div>

                            <div>
                                <label htmlFor="phone-number" className={labelStyles}>
                                    Phone Number <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        id="phone-number"
                                        type="tel"
                                        value={mobile}
                                        onChange={handlePhoneChange}
                                        placeholder="Enter your phone number (e.g. 9876543210)"
                                        className={`${inputStyles} ${
                                            isPhoneFieldDisabled() ? 'bg-gray-100 cursor-not-allowed' : ''
                                        } ${phoneAlreadyExists ? 'border-red-500 focus:ring-red-500' : ''}`}
                                        disabled={isPhoneFieldDisabled()}
                                        readOnly={!isPhoneFieldEditable() && !phoneAlreadyExists}
                                    />
                                    {phoneAlreadyExists && (
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                                        </div>
                                    )}
                                </div>
                                <p className={`text-xs mt-1 ${phoneAlreadyExists ? 'text-red-500' : 'text-gray-500'}`}>
                                    {getHelperText()}
                                </p>
                            </div>

                            {/* No Phone Number Info */}
                            {!hasPhoneInProfile && !isPhoneVerified && !otpSent && (
                                <div className="rounded-md bg-blue-50 border border-blue-200 p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <PhoneIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-blue-800">No Phone Number Found</h3>
                                            <div className="mt-2 text-sm text-blue-700">
                                                <p>
                                                    Your account doesn't have a phone number yet. 
                                                    Please enter your phone number below to add and verify it.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Phone Already Exists Warning */}
                            {phoneAlreadyExists && (
                                <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-yellow-800">Phone Number Already Registered</h3>
                                            <div className="mt-2 text-sm text-yellow-700">
                                                <p>
                                                    This phone number is already associated with another account.
                                                    Please enter a different phone number to continue.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Already Verified Message */}
                            {isPhoneVerified && !phoneVerifiedRedux && (
                                <div className="rounded-md bg-green-50 border border-green-200 p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-green-800">Phone Already Verified</h3>
                                            <div className="mt-2 text-sm text-green-700">
                                                <p>Your phone number is already verified. You can go back to the dashboard.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!isPhoneVerified && (
                                <div>
                                    <button
                                        onClick={sendOtp}
                                        disabled={loading || (otpSent && !phoneAlreadyExists) || isPhoneVerified || !mobile.toString().trim()}
                                        className={`${buttonBaseStyles} ${loading
                                            ? "opacity-50 cursor-not-allowed bg-gray-600"
                                            : isPhoneVerified
                                                ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                                                : !mobile.toString().trim()
                                                    ? "opacity-50 cursor-not-allowed bg-gray-400"
                                                    : phoneAlreadyExists
                                                        ? "bg-orange-600 hover:bg-orange-700 focus:ring-orange-500"
                                                        : "bg-indigo-600 hover:bg-blue-700 focus:ring-indigo-500"
                                            }`}
                                    >
                                        {loading ? (
                                            <>
                                                <svg
                                                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                {!hasPhoneInProfile ? "Checking & Sending OTP..." : "Sending OTP..."}
                                            </>
                                        ) : isPhoneVerified ? (
                                            "Verified"
                                        ) : otpSent && !phoneAlreadyExists ? (
                                            "OTP Sent"
                                        ) : phoneAlreadyExists ? (
                                            "Try Different Number"
                                        ) : (
                                            "Send Verification Code"
                                        )}
                                    </button>
                                </div>
                            )}

                            {otpSent && !isPhoneVerified && !phoneAlreadyExists && (
                                <>
                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-gray-200"></div>
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="px-2 bg-white text-gray-500">Enter verification code</span>
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="otp" className={labelStyles}>
                                            Verification Code <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="otp"
                                            type="text"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            placeholder="Enter the 6-digit OTP"
                                            className={inputStyles}
                                            maxLength="6"
                                            disabled={loading}
                                        />
                                    </div>
                                    
                                    {/* Resend OTP Section */}
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-500">
                                            {resendTimer > 0 ? (
                                                <span>Resend OTP in <span className="font-medium text-indigo-600">{formatTime(resendTimer)}</span></span>
                                            ) : (
                                                <span>Didn't receive the code?</span>
                                            )}
                                        </div>
                                        <button
                                            onClick={resendOtp}
                                            disabled={!canResend || loading}
                                            className={`flex items-center text-sm font-medium ${
                                                canResend && !loading
                                                    ? "text-indigo-600 hover:text-indigo-500 cursor-pointer"
                                                    : "text-gray-400 cursor-not-allowed"
                                            }`}
                                        >
                                            <ArrowPathIcon className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                                            Resend OTP
                                        </button>
                                    </div>

                                    <div>
                                        <button
                                            onClick={verifyOtp}
                                            disabled={loading}
                                            className={`${buttonBaseStyles} ${loading
                                                ? "opacity-50 cursor-not-allowed bg-gray-600"
                                                : "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                                                }`}
                                        >
                                            {loading ? (
                                                <>
                                                    <svg
                                                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        ></circle>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                        ></path>
                                                    </svg>
                                                    Verifying...
                                                </>
                                            ) : (
                                                "Verify OTP"
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}

                            {error && !phoneAlreadyExists && (
                                <div className="rounded-md bg-red-50 p-4">
                                    <div className="flex">
                                        <div className="ml-3">
                                            <p className="text-sm text-red-700">{error}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {successMessage && (
                                <div className="rounded-md bg-green-50 p-4">
                                    <div className="flex">
                                        <div className="ml-3">
                                            <p className="text-sm text-green-700">{successMessage}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">Or</span>
                            </div>
                        </div>

                        <div>
                            <button
                                onClick={handleGoBack}
                                className="w-full flex justify-center items-center h-12 px-6 border border-gray-300 rounded-lg text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 ease-in-out"
                            >
                                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                Go Back to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
                <div id="recaptcha-container" className="mt-8 flex justify-center"></div>
            </div>
        </div>
    )
}

export default PhoneVerificationPhone